/**
 * Rect defines and processes rectangular regions
 */
export class Rect {
    constructor(x, y, width, height) {
        /**
         * Sets the x-coordinate of the starting point of a rectangular region
         * @default 0
         */
        this.x = Number.MAX_VALUE;
        /**
         * Sets the y-coordinate of the starting point of a rectangular region
         * @default 0
         */
        this.y = Number.MAX_VALUE;
        /**
         * Sets the width of a rectangular region
         * @default 0
         */
        this.width = 0;
        /**
         * Sets the height of a rectangular region
         * @default 0
         */
        this.height = 0;
        if (x === undefined || y === undefined) {
            x = y = Number.MAX_VALUE;
            width = height = 0;
        }
        else {
            if (width === undefined) {
                width = 0;
            }
            if (height === undefined) {
                height = 0;
            }
        }
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
    }
    /**   @private  */
    get left() {
        return this.x;
    }
    /**   @private  */
    get right() {
        return this.x + this.width;
    }
    /**   @private  */
    get top() {
        return this.y;
    }
    /**   @private  */
    get bottom() {
        return this.y + this.height;
    }
    /**   @private  */
    get topLeft() {
        return { x: this.left, y: this.top };
    }
    /**   @private  */
    get topRight() {
        return { x: this.right, y: this.top };
    }
    /**   @private  */
    get bottomLeft() {
        return { x: this.left, y: this.bottom };
    }
    /**   @private  */
    get bottomRight() {
        return { x: this.right, y: this.bottom };
    }
    /**   @private  */
    get middleLeft() {
        return { x: this.left, y: this.y + this.height / 2 };
    }
    /**   @private  */
    get middleRight() {
        return { x: this.right, y: this.y + this.height / 2 };
    }
    /**   @private  */
    get topCenter() {
        return { x: this.x + this.width / 2, y: this.top };
    }
    /**   @private  */
    get bottomCenter() {
        return { x: this.x + this.width / 2, y: this.bottom };
    }
    /**   @private  */
    get center() {
        return { x: this.x + this.width / 2, y: this.y + this.height / 2 };
    }
    /**   @private  */
    equals(rect1, rect2) {
        return rect1.x === rect2.x && rect1.y === rect2.y && rect1.width === rect2.width && rect1.height === rect2.height;
    }
    /**   @private  */
    uniteRect(rect) {
        let right = Math.max(Number.NaN === this.right || this.x === Number.MAX_VALUE ? rect.right : this.right, rect.right);
        let bottom = this.bottom;
        this.x = Math.min(this.left, rect.left);
        this.y = Math.min(this.top, rect.top);
        this.width = right - this.x;
        this.height = bottom - this.y;
        return this;
    }
    /**   @private  */
    unitePoint(point) {
        if (this.x === Number.MAX_VALUE) {
            this.x = point.x;
            this.y = point.y;
            return;
        }
        let x = Math.min(this.left, point.x);
        let y = Math.min(this.top, point.y);
        let right = Math.max(this.right, point.x);
        let bottom = Math.max(this.bottom, point.y);
        this.x = x;
        this.y = y;
        this.width = right - this.x;
        this.height = bottom - this.y;
    }
    intersection(rect) {
        if (this.intersects(rect)) {
            let left = Math.max(this.left, rect.left);
            let top = Math.max(this.top, rect.top);
            let right = Math.min(this.right, rect.right);
            let bottom = Math.min(this.bottom, rect.bottom);
            return new Rect(left, top, right - left, bottom - top);
        }
        return Rect.empty;
    }
    /**   @private  */
    Inflate(padding) {
        this.x -= padding;
        this.y -= padding;
        this.width += padding * 2;
        this.height += padding * 2;
        return this;
    }
    // public Inflate(size: Size): Rect {
    //    this.x -= size.Width;
    //    this.y -= size.Height;
    //    this.width += size.Width * 2;
    //    this.height += size.Height * 2;
    //    return this;
    // }
    // public inflate(width: number, height: number): void {
    //     this.x -= width;
    //     this.y -= height;
    //     this.width += width * 2;
    //     this.height += height * 2;
    // }
    /**   @private  */
    intersects(rect) {
        if (this.right < rect.left || this.left > rect.right || this.top > rect.bottom || this.bottom < rect.top) {
            return false;
        }
        return true;
    }
    /**   @private  */
    containsRect(rect) {
        return this.left <= rect.left && this.right >= rect.right && this.top <= rect.top && this.bottom >= rect.bottom;
    }
    /**   @private  */
    containsPoint(point, padding = 0) {
        return this.left - padding <= point.x && this.right + padding >= point.x
            && this.top - padding <= point.y && this.bottom + padding >= point.y;
    }
    toPoints() {
        let points = [];
        points.push(this.topLeft);
        points.push(this.topRight);
        points.push(this.bottomLeft);
        points.push(this.bottomRight);
        return points;
    }
    /**   @private  */
    static toBounds(points) {
        let rect = new Rect();
        for (let pt of points) {
            rect.unitePoint(pt);
        }
        return rect;
    }
    scale(scaleX, scaleY) {
        this.width *= scaleX;
        this.height *= scaleY;
    }
    offset(offsetX, offsetY) {
        this.x += offsetX;
        this.y += offsetY;
    }
}
/**   @private  */
Rect.empty = new Rect(Number.MAX_VALUE, Number.MIN_VALUE, 0, 0);
